import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';

/**
 * Hook para obtener y escuchar un documento específico en tiempo real
 * @param {string} collectionName - Nombre de la colección
 * @param {string} docId - ID del documento (null para no cargar)
 * @param {boolean} realtime - Si debe escuchar cambios en tiempo real
 */
export const useDocument = (collectionName, docId = null, realtime = false) => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exists, setExists] = useState(false);
  const { userProfile } = useAuth();

  const fetchDocument = useCallback(async () => {
    if (!collectionName || !docId) {
      setDocument(null);
      setExists(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setDocument(data);
        setExists(true);
      } else {
        setDocument(null);
        setExists(false);
        setError('Documento no encontrado');
      }
    } catch (err) {
      console.error(`Error fetching document from ${collectionName}:`, err);
      setError(err.message);
      setDocument(null);
      setExists(false);
    } finally {
      setLoading(false);
    }
  }, [collectionName, docId]);

  // Carga inicial y cuando cambian los parámetros
  useEffect(() => {
    if (!realtime) {
      fetchDocument();
    }
  }, [fetchDocument, realtime]);

  // Escucha en tiempo real
  useEffect(() => {
    if (!realtime || !collectionName || !docId) return;

    setLoading(true);
    const docRef = doc(db, collectionName, docId);
    
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          setDocument(data);
          setExists(true);
        } else {
          setDocument(null);
          setExists(false);
        }
        setLoading(false);
      },
      (err) => {
        console.error(`Error listening to document in ${collectionName}:`, err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [collectionName, docId, realtime]);

  // Función helper para comprobar permisos
  const canEdit = useCallback(() => {
    if (!document || !userProfile) return false;
    
    // Admin puede editar todo
    if (userProfile.role === 'admin') return true;
    
    // El creador puede editar
    if (document.createdBy === userProfile.uid) return true;
    
    // Si está asignado al usuario, puede editar
    if (document.assignedTo === userProfile.uid) return true;
    
    return false;
  }, [document, userProfile]);

  // Función helper para comprobar si el documento está activo
  const isActive = useCallback(() => {
    if (!document) return false;
    return document.isActive !== false; // Si no tiene el campo, asumimos activo
  }, [document]);

  return {
    document,
    loading,
    error,
    exists,
    canEdit: canEdit(),
    isActive: isActive(),
    refresh: fetchDocument
  };
};

export default useDocument;